import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { API } from 'aws-amplify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

export interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: React.ReactNode | string;
  trend?: number;
  isFallback?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
  isFallback = false,
}) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'visible',
      opacity: isFallback ? 0.7 : 1,
      border: isFallback ? '2px dashed' : 'none',
      borderColor: isFallback ? 'warning.main' : 'transparent',
    }}
  >
    <CardContent>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              color: `${color}.main`,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {title}
          </Typography>
          {isFallback && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                borderRadius: 0.5,
                fontSize: '0.625rem',
                fontWeight: 500,
                ml: 1,
              }}
            >
              FAILED
            </Box>
          )}
        </Box>
      </Box>

      <Typography variant="h4" fontWeight="bold" color={`${color}.main`} mb={1}>
        {value}
      </Typography>

      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}

      {isFallback && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ fontStyle: 'italic' }}
        >
          Warning: Metric failed to load
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Format a JS Date object into MM/DD/YYYY string
 */
export function formatDateMMDDYYYY(date: Date | null): string | null {
  if (!date) return null;
  const month = date.getMonth() + 1; // months are 0-based
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Wrapper function to make Amplify GraphQL calls cancellable
 */
function cancellableGraphQLQuery<T>(
  query: any,
  variables: any,
  abortSignal?: AbortSignal
): Promise<T> {
  return new Promise((resolve, reject) => {
    // Check if already aborted
    if (abortSignal?.aborted) {
      reject(new Error('Request aborted'));
      return;
    }

    // Make the GraphQL call
    const graphqlPromise = API.graphql({
      query,
      variables,
    });

    let abortListener: () => void;

    // Set up abort listener if signal is provided
    if (abortSignal) {
      abortListener = () => {
        reject(new Error('Request aborted'));
      };
      abortSignal.addEventListener('abort', abortListener, { once: true });
    }

    // Handle the GraphQL response
    Promise.resolve(graphqlPromise)
      .then((response: any) => {
        if (!abortSignal?.aborted) {
          resolve(response as T);
        }
      })
      .catch((error: any) => {
        if (!abortSignal?.aborted) {
          reject(error);
        }
      })
      .finally(() => {
        // Clean up abort listener
        if (abortSignal && abortListener) {
          abortSignal.removeEventListener('abort', abortListener);
        }
      });
  });
}

/**
 * Helper function to parse the nested JSON response from GraphQL queries
 */
const parseResponse = (response: any, key: string) => {
  try {
    const responseStr = response?.data?.[key];
    if (!responseStr) {
      console.error(`No response data found for metric ${key}:`, response);
      return null;
    }

    const parsed = JSON.parse(responseStr);
    if (parsed.statusCode === 200 && parsed.body) {
      const body = JSON.parse(parsed.body);
      return body;
    } else {
      // Log non-200 status codes and their details
      console.error(`Metric ${key} returned non-200 status:`, {
        statusCode: parsed.statusCode,
        body: parsed.body,
        headers: parsed.headers,
        errorMessage: parsed.errorMessage || 'No error message provided',
      });
      return null;
    }
  } catch (error) {
    console.error(`Error parsing response for ${key}:`, error);
    console.error(`Raw response data for ${key}:`, response);
    return null;
  }
};

export const fetchAllMetrics = async (
  organization: string,
  startDate: Date | null,
  endDate: Date | null,
  abortSignal: AbortSignal,
  setError: (error: boolean) => void,
  retryFailedOnly: boolean = false,
  previousFailedMetrics: string[] = [],
  existingMetricsData?: any,
  circleId?: string,
  userId?: string,
  granularity?: 'daily' | 'weekly' | 'monthly'
) => {
  const formattedStartDate = formatDateMMDDYYYY(startDate);
  const formattedEndDate = formatDateMMDDYYYY(endDate);

  console.log(granularity);

  // Check if aborted before starting
  if (abortSignal?.aborted) {
    throw new Error('Request aborted');
  }

  try {
    const requestVariables = {
      request: JSON.stringify({
        orgid: organization,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        ...(circleId && { circleid: circleId }),
        ...(userId && { userid: userId }),
      }),
    };

    // Define all metric queries
    const metricQueries: any[] = [];
    // Note: all CS dashboard metric query services have been removed from graphql/queries

    // Helper function to convert existing metric values back to raw data format
    const convertExistingValueToRawData = (key: string, value: any) => {
      // Convert the processed metric value back to the raw data format expected by getMetricValue
      switch (key) {
        case 'logins':
          return { num_logins: value };
        case 'pinsCreated':
          return { num_pins: value };
        case 'dealsCreated':
          return { num_deals_created: value };
        case 'dealStageChanges':
          return { num_deals_stage_changes: value };
        case 'recordingsTaken':
          return { num_recordings_taken: value };
        case 'loginPercentageVsTotalUsers':
          return {
            all_users_login_rate: value / 100,
          };
        case 'loginPercentageVsActiveUsers':
          return { active_users_login_rate: value / 100 };
        case 'totalAllUsers':
          return { total_all_users: value };
        case 'totalActiveUsers':
          return { total_active_users: value };
        case 'activeUsersWithLoginCount':
          return { active_users_with_login_count: value };
        case 'imagesUploaded':
          return { num_images_uploaded: value };
        case 'avgTimeInApp':
          return { avg_daily_time_minutes: value };
        case 'outdatedUsers':
          return { out_of_date_users: value };
        case 'loginPercentageVsSeats':
          return { login_percentage: value / 100, org_seats: value };
        case 'askPaivQuestions':
          return { num_ask_paiv: value };
        case 'peakActivityTime':
          if (value && typeof value === 'object') {
            // If it's already in the correct format, preserve it
            if (value.window_Start && value.window_End) {
              return value;
            }
            // If it has a body property, extract the actual data
            if (value.body) {
              if (typeof value.body === 'string') {
                try {
                  return JSON.parse(value.body);
                } catch {
                  return value;
                }
              } else if (typeof value.body === 'object') {
                return value.body;
              }
            }
            return value;
          } else if (typeof value === 'string') {
            // If it's a string, try to parse it
            try {
              return JSON.parse(value);
            } catch {
              return { peak_activity_time: value };
            }
          }
          // Fallback for any other case
          return { peak_activity_time: value };
        default:
          return null;
      }
    };

    // If retrying failed metrics only, filter to just those for execution
    // but we still need to process all metrics for the final result
    const queriesToExecute = retryFailedOnly
      ? metricQueries.filter((_, index) =>
          previousFailedMetrics.includes(metricQueries[index].name)
        )
      : metricQueries;

    const results: PromiseSettledResult<any>[] = [];
    const batchSize = 4; // Process 4 metrics at a time

    for (let i = 0; i < queriesToExecute.length; i += batchSize) {
      const batch = queriesToExecute.slice(i, i + batchSize);

      // Check if aborted before each batch
      if (abortSignal?.aborted) {
        throw new Error('Request aborted');
      }

      const batchResults = await Promise.allSettled(
        batch.map((metric) =>
          cancellableGraphQLQuery(metric.query, requestVariables, abortSignal)
        )
      );

      results.push(...batchResults);

      // Add small delay between batches to reduce resource contention
      if (i + batchSize < queriesToExecute.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Check if aborted before processing results
    if (abortSignal?.aborted) {
      throw new Error('Request aborted');
    }

    // Track which metrics failed and succeeded
    const metricNames = metricQueries.map((q) => q.name);
    const failedMetrics: string[] = [];
    const successfulMetrics: string[] = [];

    // Initialize processedData with all metric names
    const processedData: { [key: string]: any } = {};
    metricQueries.forEach((metric) => {
      processedData[metric.name] = null;
    });

    // Process the executed queries (either all or just failed ones)
    queriesToExecute.forEach((metric, index) => {
      const result = results[index];
      const data =
        result.status === 'fulfilled'
          ? parseResponse(result.value, metric.key)
          : null;

      if (result.status === 'rejected') {
        failedMetrics.push(metric.name);
        console.error(`Failed to fetch ${metric.name}:`, result.reason);

        // Log detailed error information from the endpoint/lambda
        if (result.reason?.errors) {
          console.error(
            `GraphQL errors for ${metric.name}:`,
            result.reason.errors
          );
        }
        if (result.reason?.data) {
          console.error(
            `Response data for ${metric.name}:`,
            result.reason.data
          );
        }
        if (result.reason?.extensions) {
          console.error(
            `Extensions for ${metric.name}:`,
            result.reason.extensions
          );
        }

        processedData[metric.name] = null;
      } else if (data) {
        successfulMetrics.push(metric.name);
        processedData[metric.name] = data;
      } else {
        failedMetrics.push(metric.name);
        console.error(
          `Metric ${metric.name} returned no data or failed parsing`
        );
        processedData[metric.name] = null;
      }
    });

    // If this is a retry of failed metrics, merge with existing successful data
    if (
      retryFailedOnly &&
      previousFailedMetrics.length > 0 &&
      existingMetricsData
    ) {
      // Use the passed existing metrics data
      const existingMetrics = existingMetricsData;

      // Create a mapping from metric names to the final object keys
      const metricNameToKeyMap: { [key: string]: string } = {
        'User Logins': 'logins',
        'Pins Created': 'pinsCreated',
        'Deals Created': 'dealsCreated',
        'Deal Stage Changes': 'dealStageChanges',
        'Recordings Taken': 'recordingsTaken',
        'Login Percentage vs Total Users': 'loginPercentageVsTotalUsers',
        'Images Uploaded': 'imagesUploaded',
        'Average Time in App': 'avgTimeInApp',
        'Outdated Users': 'outdatedUsers',
        'Login Percentage vs Seat Count': 'loginPercentageVsSeats',
        'Ask Paiv Questions': 'askPaivQuestions',
        'Peak Activity Time': 'peakActivityTime',
      };

      // Create a reverse mapping for easier lookup
      const keyToMetricNameMap: { [key: string]: string } = {};
      Object.entries(metricNameToKeyMap).forEach(([metricName, key]) => {
        keyToMetricNameMap[key] = metricName;
      });

      // Merge retry results with existing successful data
      Object.keys(processedData).forEach((metricName) => {
        if (processedData[metricName] !== null) {
          // This metric succeeded in retry, remove from failed list
          const index = failedMetrics.indexOf(metricName);
          if (index > -1) {
            failedMetrics.splice(index, 1);
            successfulMetrics.push(metricName);
          }
        }
      });

      // Add back previously successful metrics that weren't retried
      metricQueries.forEach((metric) => {
        if (
          !previousFailedMetrics.includes(metric.name) &&
          !successfulMetrics.includes(metric.name)
        ) {
          successfulMetrics.push(metric.name);
        }
      });

      // Merge the processed data with existing successful data for metrics that weren't retried
      if (existingMetrics._metadata?.successfulMetrics) {
        existingMetrics._metadata.successfulMetrics.forEach(
          (metricName: string) => {
            if (
              !previousFailedMetrics.includes(metricName) &&
              !processedData[metricName]
            ) {
              // This metric was previously successful and wasn't retried, preserve its success status
              if (!successfulMetrics.includes(metricName)) {
                successfulMetrics.push(metricName);
              }
            }
          }
        );
      }

      // Merge the actual metric values - preserve existing successful values for metrics not being retried

      // Special handling for Login Percentage vs Total Users metric which contains multiple data points
      const loginPercentageMetricName = 'Login Percentage vs Total Users';
      if (!processedData[loginPercentageMetricName]) {
        processedData[loginPercentageMetricName] = {};
      }

      // Collect all the login-related data into the single metric
      if (existingMetrics.loginPercentageVsActiveUsers !== undefined) {
        processedData[loginPercentageMetricName].active_users_login_rate =
          existingMetrics.loginPercentageVsActiveUsers;
      }
      if (existingMetrics.loginPercentageVsTotalUsers !== undefined) {
        processedData[loginPercentageMetricName].all_users_login_rate =
          existingMetrics.loginPercentageVsTotalUsers;
      }
      if (existingMetrics.totalAllUsers !== undefined) {
        processedData[loginPercentageMetricName].total_all_users =
          existingMetrics.totalAllUsers;
      }
      if (existingMetrics.totalActiveUsers !== undefined) {
        processedData[loginPercentageMetricName].total_active_users =
          existingMetrics.totalActiveUsers;
      }
      if (existingMetrics.activeUsersWithLoginCount !== undefined) {
        processedData[loginPercentageMetricName].active_users_with_login_count =
          existingMetrics.activeUsersWithLoginCount;
      }

      // Special handling for Login Percentage vs Seat Count metric which also contains multiple data points
      const loginPercentageSeatsMetricName = 'Login Percentage vs Seat Count';
      if (!processedData[loginPercentageSeatsMetricName]) {
        processedData[loginPercentageSeatsMetricName] = {};
      }

      // Collect all the seat-related data into the single metric
      if (existingMetrics.loginPercentageVsSeats !== undefined) {
        processedData[loginPercentageSeatsMetricName].login_percentage =
          existingMetrics.loginPercentageVsSeats;
      }
      if (existingMetrics.totalSeats !== undefined) {
        processedData[loginPercentageSeatsMetricName].org_seats =
          existingMetrics.totalSeats;
      }

      // Handle other metrics normally
      Object.keys(existingMetrics).forEach((key) => {
        if (
          key !== '_metadata' &&
          key !== 'loginPercentageVsActiveUsers' &&
          key !== 'loginPercentageVsTotalUsers' &&
          key !== 'totalAllUsers' &&
          key !== 'totalActiveUsers' &&
          key !== 'activeUsersWithLoginCount' &&
          key !== 'loginPercentageVsSeats' &&
          key !== 'totalSeats' &&
          existingMetrics[key] !== undefined
        ) {
          const metricName = keyToMetricNameMap[key];
          if (metricName && !processedData[metricName]) {
            processedData[metricName] = convertExistingValueToRawData(
              key,
              existingMetrics[key]
            );
          }
        }
      });
    }

    if (failedMetrics.length > 0) {
      console.log('Failed metrics:', failedMetrics);
    }

    // Helper function to extract metric value safely
    const getMetricValue = (
      metricName: string,
      dataKey: string,
      fallback: any = 0
    ) => {
      const data = processedData[metricName];
      if (!data) return fallback;

      // If dataKey is empty, return the entire data object
      if (!dataKey || dataKey.trim() === '') {
        return data;
      }

      const keys = dataKey.split('.');
      let value = data;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) return fallback;
      }
      return value;
    };

    // Return partial data - use fallback values for failed metrics
    return {
      logins: getMetricValue('User Logins', 'num_logins', 0),
      pinsCreated: getMetricValue('Pins Created', 'num_pins', 0),
      dealsCreated: getMetricValue('Deals Created', 'num_deals_created', 0),
      dealStageChanges: getMetricValue(
        'Deal Stage Changes',
        'num_deals_stage_changes',
        0
      ),
      recordingsTaken: getMetricValue(
        'Recordings Taken',
        'num_recordings_taken',
        0
      ),
      loginPercentageVsActiveUsers: getMetricValue(
        'Login Percentage vs Total Users',
        'active_users_login_rate',
        0
      ),
      loginPercentageVsTotalUsers: getMetricValue(
        'Login Percentage vs Total Users',
        'all_users_login_rate',
        0
      ),
      totalAllUsers: getMetricValue(
        'Login Percentage vs Total Users',
        'total_all_users',
        0
      ),
      totalActiveUsers: getMetricValue(
        'Login Percentage vs Total Users',
        'total_active_users',
        0
      ),
      activeUsersWithLoginCount: getMetricValue(
        'Login Percentage vs Total Users',
        'active_users_with_login_count',
        0
      ),
      imagesUploaded: getMetricValue(
        'Images Uploaded',
        'num_images_uploaded',
        0
      ),
      avgTimeInApp: (() => {
        const value = getMetricValue(
          'Average Time in App',
          'avg_daily_time_minutes',
          0
        );
        return value ? Number(value.toFixed(2)) : 0;
      })(),
      outdatedUsers: getMetricValue('Outdated Users', 'out_of_date_users', 0),
      loginPercentageVsSeats: (() => {
        const value = getMetricValue(
          'Login Percentage vs Seat Count',
          'login_percentage',
          0
        );
        return value ? Number(value.toFixed(2)) : 0;
      })(),
      totalSeats: getMetricValue(
        'Login Percentage vs Seat Count',
        'org_seats',
        0
      ),
      askPaivQuestions: getMetricValue('Ask Paiv Questions', 'num_ask_paiv', 0),
      peakActivityTime: getMetricValue('Peak Activity Time', '', null),
      // Add metadata about what succeeded/failed
      _metadata: {
        successfulMetrics,
        failedMetrics,
        totalMetrics: metricNames.length,
        hasPartialData: successfulMetrics.length > 0,
        allFailed: successfulMetrics.length === 0,
        isInitialLoadComplete: !retryFailedOnly,
      },
    };
  } catch (error) {
    if (
      abortSignal?.aborted ||
      (error as Error)?.message === 'Request aborted'
    ) {
      throw new Error('Request aborted');
    }

    // Enhanced error logging for endpoint/lambda failures
    console.error('Error fetching metrics:', error);

    setError(true);
    return null;
  }
};

/**
 * Fetch trends data for a date range with granularity support
 * Returns data formatted for charts with proper labels based on granularity
 */
export const fetchTrendsData = async (
  organization: string,
  startDate: Date | null,
  endDate: Date | null,
  abortSignal: AbortSignal,
  circleId?: string,
  userId?: string,
  granularity: 'daily' | 'weekly' | 'monthly' = 'weekly'
): Promise<{ data: any; labels: string[] } | null> => {
  // Check if we have appropriate data for the granularity
  if (!startDate || !endDate) {
    return null;
  }

  const timeDiff = endDate.getTime() - startDate.getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;
  const oneWeekMs = 7 * oneDayMs;
  const threeMonthsMs = 90 * oneDayMs;
  const oneYearMs = 365 * oneDayMs;

  // Validate date range for granularity
  let isValidRange = false;
  switch (granularity) {
    case 'daily':
      isValidRange = timeDiff <= oneWeekMs;
      break;
    case 'weekly':
      isValidRange = timeDiff <= 5 * oneWeekMs;
      break;
    case 'monthly':
      isValidRange = timeDiff >= threeMonthsMs && timeDiff <= oneYearMs;
      break;
  }

  if (!isValidRange) {
    console.warn(`Date range not appropriate for ${granularity} granularity`);
    return null;
  }

  // Check if aborted before starting
  if (abortSignal?.aborted) {
    throw new Error('Request aborted');
  }

  try {
    // Fetch all metrics with granularity support
    // Note: all trend metric query services have been removed from graphql/queries
    // Return empty data structure until services are restored
    return null;
  } catch (error) {
    if (
      abortSignal?.aborted ||
      (error as Error)?.message === 'Request aborted'
    ) {
      throw new Error('Request aborted');
    }

    console.error('Error fetching trends data:', error);
    return null;
  }
};

// Utility function to format peak activity time
export const formatPeakActivityTime = (
  peakActivityData: any,
  timezone: string = 'America/Denver',
  depth: number = 0
): string => {
  if (!peakActivityData || depth > 2) {
    return 'Data unavailable';
  }

  try {
    let windowStart, windowEnd;

    // Case 1: Already an object with window_Start/window_End
    if (peakActivityData.window_Start && peakActivityData.window_End) {
      windowStart = peakActivityData.window_Start;
      windowEnd = peakActivityData.window_End;
    }
    // Case 2: object with .body as string
    else if (
      peakActivityData.body &&
      typeof peakActivityData.body === 'string'
    ) {
      const parsed = JSON.parse(peakActivityData.body);
      windowStart = parsed.window_Start;
      windowEnd = parsed.window_End;
    }
    // Case 3: object with .body as object
    else if (
      peakActivityData.body &&
      typeof peakActivityData.body === 'object'
    ) {
      windowStart = peakActivityData.body.window_Start;
      windowEnd = peakActivityData.body.window_End;
    }
    // Case 4: top-level string, try parsing it and recurse
    else if (typeof peakActivityData === 'string') {
      const maybeParsed = JSON.parse(peakActivityData);
      return formatPeakActivityTime(maybeParsed, timezone, depth + 1);
    }

    if (!windowStart || !windowEnd) {
      return 'Data unavailable';
    }

    // Helper: UTC to specified timezone
    const toTimezone = (timeStr: string, tz: string) => {
      const [hours, minutes, seconds] = timeStr.split(':').map(Number);
      // Use explicit UTC date for robust TZ conversion
      const utcDate = new Date(Date.UTC(2000, 0, 1, hours, minutes, seconds));
      return utcDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
      });
    };

    const startTime = toTimezone(windowStart, timezone);
    const endTime = toTimezone(windowEnd, timezone);

    // Get timezone abbreviation
    const timezoneAbbr = getTimezoneAbbreviation(timezone);
    return `${startTime} - ${endTime} ${timezoneAbbr}`;
  } catch (e) {
    console.error('Error formatting peak activity time:', e);
    return 'Data unavailable';
  }
};

/**
 * Fetch login trends for a date range, splitting it into 4 chunks
 * Returns both the login data and date labels for the chart
 */
export const fetchLoginTrends = async (
  organization: string,
  startDate: Date | null,
  endDate: Date | null,
  abortSignal: AbortSignal,
  _circleId?: string,
  _userId?: string
): Promise<{ data: number[]; labels: string[] } | null> => {
  // Check if we have appropriate data for the granularity
  if (!startDate || !endDate) {
    return null;
  }

  const timeDiff = endDate.getTime() - startDate.getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;
  const oneWeekMs = 7 * oneDayMs;
  const oneMonthMs = 30 * oneDayMs;
  const threeMonthsMs = 90 * oneDayMs;
  const oneYearMs = 365 * oneDayMs;

  console.log(oneWeekMs, oneMonthMs, threeMonthsMs, oneYearMs);

  // Default behavior: require at least one month of data
  if (timeDiff < oneMonthMs) {
    return null;
  }

  // Check if aborted before starting
  if (abortSignal?.aborted) {
    throw new Error('Request aborted');
  }

  try {
    // Split the date range into 4 chunks (similar to original behavior)
    const datePoints: Date[] = [];
    const labels: string[] = [];
    const totalDuration = endDate.getTime() - startDate.getTime();
    const chunkDuration = totalDuration / 3; // 3 chunks, but we'll have 4 points

    for (let i = 0; i <= 3; i++) {
      const date = new Date(startDate.getTime() + chunkDuration * i);
      datePoints.push(date);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      labels.push(`${month} ${day}`);
    }

    // For the first data point, we need to go back one chunk duration from the start
    const firstChunkStart = new Date(startDate.getTime() - chunkDuration);

    // Create the date ranges for each chunk
    const dateRanges = [
      { start: firstChunkStart, end: startDate }, // First chunk (before the selected range)
      { start: startDate, end: new Date(startDate.getTime() + chunkDuration) }, // Second chunk
      {
        start: new Date(startDate.getTime() + chunkDuration),
        end: new Date(startDate.getTime() + 2 * chunkDuration),
      }, // Third chunk
      {
        start: new Date(startDate.getTime() + 2 * chunkDuration),
        end: endDate,
      }, // Fourth chunk
    ];

    // Fetch login data for each chunk
    const loginData: number[] = [];

    for (const range of dateRanges) {
      // Check if aborted before each request
      if (abortSignal?.aborted) {
        throw new Error('Request aborted');
      }

      try {
        // getNumLogins service no longer exists — login data unavailable
        loginData.push(0);
      } catch (error) {
        console.error('Error fetching login data for range:', range, error);
        loginData.push(0); // Fallback to 0 if this chunk fails
      }
    }

    return {
      data: loginData,
      labels,
    };
  } catch (error) {
    if (
      abortSignal?.aborted ||
      (error as Error)?.message === 'Request aborted'
    ) {
      throw new Error('Request aborted');
    }

    console.error('Error fetching login trends:', error);
    return null;
  }
};

// Helper function to get timezone abbreviation
export const getTimezoneAbbreviation = (timezone: string): string => {
  const timezoneMap: { [key: string]: string } = {
    'America/Denver': 'MST',
    'America/New_York': 'EST',
    'America/Chicago': 'CST',
    'America/Los_Angeles': 'PST',
    'America/Anchorage': 'AKST',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Kolkata': 'IST',
    'Australia/Sydney': 'AEST',
    UTC: 'UTC',
  };
  return (
    timezoneMap[timezone] ||
    timezone.split('/').pop()?.split('_').join('') ||
    timezone
  );
};
